import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import Snap from 'ol/interaction/Snap';
import VectorLayer from 'ol/layer/Vector';
import { Style, Stroke } from 'ol/style';
import { map } from './Map';
import { GEOSERVER_WFS_URL, LAYERS, MAP_PROJECTION_CODE } from './Config';

/**
 * Snapping.js - Robust Snapping with Custom Loader & Interaction Syncing
 */

let snapSource = null;
let snapLayer = null;
let snapInteraction = null;
let isSnappingEnabled = false;
window.isSnappingEnabled = false;
const MIN_SNAP_ZOOM = 10;
// Scale 1:1600 roughly equals 0.45 resolution in EPSG:28191 (Meters)
const MAX_SNAP_RESOLUTION = 0.45;
window.MAX_SNAP_RESOLUTION = MAX_SNAP_RESOLUTION;
const isMobileDevice = window.innerWidth <= 1024;

function initSnapping() {
    if (snapSource) return;

    // 1. Custom Loader for Complete Control
    // This replaces the simple 'url' + 'format' approach to fix re-fetch issues
    snapSource = new VectorSource({
        strategy: bboxStrategy,
        loader: function (extent, resolution, projection, success, failure) {
            const zoom = map.getView().getZoom();

            // Check zoom level before fetching
            if (zoom < MIN_SNAP_ZOOM) {
                // If zoomed out, don't fetch, clear existing
                // console.log(`[Snapping] Zoom ${zoom.toFixed(1)} < ${MIN_SNAP_ZOOM}. Skipping WFS.`);
                if (snapSource.getFeatures().length > 0) {
                    snapSource.clear();
                }
                if (success) success([]); // Treat as successful empty load
                return;
            }

            // 2. SMART DEBOUNCE & CONTEXT CHECK
            // Wait 1 second: The "Sweet Spot". Prevents requests while panning, 
            // but feels instant when you stop to draw.
            if (window._snapLoaderTimer) clearTimeout(window._snapLoaderTimer);

            window._snapLoaderTimer = setTimeout(() => {
                performSmartFetch(extent, success, failure);
            }, 1000);
        }
    });

    // Helper: Perform Fetch only if moved significantly
    function performSmartFetch(extent, success, failure) {
        const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

        // Smart Check: Have we moved enough? (e.g., 50 meters)
        if (window._lastSnapCenter) {
            const dx = center[0] - window._lastSnapCenter[0];
            const dy = center[1] - window._lastSnapCenter[1];
            const dist = Math.sqrt(dx * dx + dy * dy);

            // If moved less than 50 meters, skip fetch (preserve server)
            if (dist < 50) {
                // console.log('[Snapping] Movement too small (<50m). Skipping fetch.');
                if (success) success([]);
                return;
            }
        }

        // Update last center
        window._lastSnapCenter = center;

        // Clear old features to keep memory low (User asked for "20 items in THE PLACE where he is")
        if (snapSource) snapSource.clear();

        // DYNAMICALLY IDENTIFY ACTIVE LAYERS
        const activeTypeNames = [];
        const mapLayers = map.getLayers().getArray();

        Object.values(LAYERS).forEach(layerConfig => {
            if (!layerConfig.fullName) return;
            const mapLayer = mapLayers.find(l => l.get('name') === layerConfig.name);
            if (mapLayer && mapLayer.getVisible()) {
                activeTypeNames.push(layerConfig.fullName);
            }
        });

        if (activeTypeNames.length === 0) {
            if (success) success([]);
            return;
        }

        // console.log('[Snapping] Fetching new data (Max 300/layer)...');

        // Use Promise.all to fetch each layer individually
        // الخريطة الآن بنظام EPSG:28191 - لا نحتاج تحويل
        const requests = activeTypeNames.map(typeName => {
            const url = GEOSERVER_WFS_URL +
                '?service=WFS&version=1.1.0&request=GetFeature' +
                '&typeName=' + typeName +
                '&outputFormat=application/json&srsname=' + MAP_PROJECTION_CODE +
                '&maxFeatures=150' +
                '&bbox=' + extent.join(',') + ',' + MAP_PROJECTION_CODE;

            const fetchFn = window.secureFetch || fetch;

            return fetchFn(url)
                .then(response => {
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response.text();
                })
                .then(text => {
                    try { return JSON.parse(text); }
                    catch (e) { return { type: 'FeatureCollection', features: [] }; }
                })
                .catch(err => {
                    console.error(`[Snapping] Failed to load ${typeName}:`, err);
                    return { type: 'FeatureCollection', features: [] };
                });
        });

        // Process all results
        Promise.all(requests)
            .then(results => {
                const allFeatures = [];
                // الخريطة الآن بنظام EPSG:28191 - نفس نظام الـ WFS
                const format = new GeoJSON({
                    dataProjection: MAP_PROJECTION_CODE,
                    featureProjection: MAP_PROJECTION_CODE
                });

                results.forEach(data => {
                    if (data && data.features) {
                        const features = format.readFeatures(data);
                        allFeatures.push(...features);
                    }
                });

                if (allFeatures.length > 0) {
                    snapSource.addFeatures(allFeatures);
                    // console.log(`[Snapping] Updated: ${allFeatures.length} features.`);
                }

                if (success) success(allFeatures);
                syncSnapInteraction();
            })
            .catch(err => {
                console.error('[Snapping] Error:', err);
                if (failure) failure();
            });
    }

    // 2. Debug Layer (Less intrusive style)
    snapLayer = new VectorLayer({
        source: snapSource,
        name: 'SnappingLayer',
        style: new Style({
            stroke: new Stroke({
                color: 'rgba(59, 130, 246, 0.5)', // Visible blue
                width: 1.5
            })
        }),
        zIndex: 9999
    });

    map.on('movestart', () => {
        if (isSnappingEnabled && window._snapLoaderTimer) {
            clearTimeout(window._snapLoaderTimer);
        }
    });

    // 4. Interaction Monitor
    // Removed recursive 'add' listener to prevent map freeze caused by infinite loop.
    // Sync will be handled manually during enable/refresh/register.

    // 5. Visibility Monitor for GIS/WFS layers
    map.getLayers().on('add', (evt) => {
        const layer = evt.element;
        if (layer && !layer.get('internal')) {
            layer.on('change:visible', () => {
                if (isSnappingEnabled) {
                    if (window._snapRefreshTimer) clearTimeout(window._snapRefreshTimer);
                    window._snapRefreshTimer = setTimeout(refreshSnapping, 200);
                }
            });
        }
    });
}


/**
 * Ensures the snap interaction is at the top of the interaction stack.
 * This ensures it has priority over other drawing/measuring interactions.
 */
function syncSnapInteraction() {
    if (snapInteraction && map) {
        const interactions = map.getInteractions();
        if (interactions.getArray().includes(snapInteraction)) {
            interactions.remove(snapInteraction);
            interactions.push(snapInteraction);
        }
    }
}


function refreshSnapping() {
    if (!isSnappingEnabled) return;

    // Refresh WFS data based on current visible layers
    if (snapSource) snapSource.refresh();
}


function enableSnapping() {
    isSnappingEnabled = true;
    window.isSnappingEnabled = true;

    initSnapping();

    // Add Layer
    if (!map.getLayers().getArray().includes(snapLayer)) {
        map.addLayer(snapLayer);
    }

    // Create Interaction - Snap فقط على النقاط (vertex) وليس على الأضلاع
    if (!snapInteraction) {
        snapInteraction = new Snap({
            source: snapSource,
            pixelTolerance: 15,
            edge: false,
            vertex: true
        });
    }

    // Add Interaction (this puts it at the end initially)
    if (!map.getInteractions().getArray().includes(snapInteraction)) {
        map.addInteraction(snapInteraction);
    }

    // Force a fresh load if we are in valid zoom
    if (map.getView().getZoom() >= MIN_SNAP_ZOOM) {
        snapSource.refresh();
    }
    
    syncSnapInteraction();
}

function disableSnapping() {
    isSnappingEnabled = false;
    window.isSnappingEnabled = false;

    if (window._snapLoaderTimer) clearTimeout(window._snapLoaderTimer);

    if (snapInteraction) {
        map.removeInteraction(snapInteraction);
        snapInteraction = null;
    }

    if (snapLayer) {
        map.removeLayer(snapLayer);
    }
}


window.refreshSnapping = refreshSnapping;

// دالة مساعدة لإخفاء جميع العدسات المكبرة
function hideAllMagnifiers() {
    const mapMagnifier = document.getElementById('map-magnifier');
    const drawMagnifier = document.getElementById('draw-magnifier');
    if (mapMagnifier) mapMagnifier.style.display = 'none';
    if (drawMagnifier) drawMagnifier.style.display = 'none';
}

// Global Toggle
window.toggleSnapping = function () {
    const $btn = $('#snappingToggle');

    // إخفاء العدسات عند التبديل
    hideAllMagnifiers();

    if (isSnappingEnabled) {
        disableSnapping();
        $btn.removeClass('active').find('i').attr('class', 'far fa-circle');
    } else {
        enableSnapping();
        $btn.addClass('active').find('i').attr('class', 'fas fa-check-circle');
    }
};

// UI Initialization
(function ensureSnappingUI() {
    $(document).ready(() => {
        if ($('#snappingToggle').length) return;

        const snapBtn = `
            <button class="sidebar-btn" id="snappingToggle" onclick="window.toggleSnapping()" title="تفعيل المغناطيس (Snap) لعقد الأراضي">
              <i class="far fa-circle"></i>
              <span>تفعيل التجاذب (Snapping)</span>
            </button>
        `;

        // Insert AFTER the tool section so settings appear directly under the button when opened
        const target = $('#drawSection').length ? $('#drawSection') : $('#measureSection');
        if (target && target.length) {
            target.after(snapBtn);
        }
    });
})();

export { enableSnapping, disableSnapping, snapSource };
